import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';

import styles from './LogCell.module.css';

const STICK_TO_BOTTOM_THRESHOLD_PX = 48;

function isNearBottom(el: HTMLElement): boolean {
  const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
  return distance <= STICK_TO_BOTTOM_THRESHOLD_PX;
}

type LogCellProps = {
  logs: string;
  isVisible: boolean;
  height: number;
  minHeight?: number;
  maxHeight?: number;
  onToggle: () => void;
  onHeightChange: (height: number) => void;
  disabled?: boolean;
};

export const LogCell: React.FC<LogCellProps> = ({
  logs,
  isVisible,
  height,
  minHeight = 100,
  maxHeight = 400,
  onToggle,
  onHeightChange,
  disabled = false,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);
  const logPreRef = useRef<HTMLPreElement>(null);
  const stickToBottomRef = useRef(true);
  const wasLogVisibleRef = useRef(false);
  const prevLogsLengthRef = useRef<number | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      e.preventDefault();
      setIsResizing(true);
      startYRef.current = e.clientY;
      startHeightRef.current = height;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    },
    [disabled, height]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaY = startYRef.current - e.clientY;
        const newHeight = startHeightRef.current + deltaY;

        if (newHeight >= minHeight && newHeight <= maxHeight) {
          onHeightChange(newHeight);
        }
      });
    },
    [isResizing, minHeight, maxHeight, onHeightChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleLogScroll = useCallback(() => {
    const pre = logPreRef.current;
    if (!pre) return;
    stickToBottomRef.current = isNearBottom(pre);
  }, []);

  const scrollLogToBottomIfPinned = useCallback(() => {
    if (!stickToBottomRef.current) return;
    const pre = logPreRef.current;
    if (!pre) return;
    pre.scrollTop = pre.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    if (isVisible && !wasLogVisibleRef.current) {
      stickToBottomRef.current = true;
    }
    wasLogVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    const prevLen = prevLogsLengthRef.current;
    const hadContent = prevLen !== null && prevLen > 0;
    const isNowEmpty = logs.length === 0;

    if (hadContent && isNowEmpty) {
      stickToBottomRef.current = true;
    }

    prevLogsLengthRef.current = logs.length;
  }, [logs]);

  useLayoutEffect(() => {
    if (!isVisible) return;

    scrollLogToBottomIfPinned();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      scrollLogToBottomIfPinned();
      raf2 = requestAnimationFrame(scrollLogToBottomIfPinned);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [logs, isVisible, scrollLogToBottomIfPinned]);

  useEffect(() => {
    if (!isVisible) return;

    const pre = logPreRef.current;
    if (!pre) return;

    const ro = new ResizeObserver(() => {
      scrollLogToBottomIfPinned();
    });
    ro.observe(pre);
    return () => ro.disconnect();
  }, [isVisible, scrollLogToBottomIfPinned]);

  return (
    <div
      className={`${styles.logFooter} ${!isVisible ? styles.logFooterCollapsed : ''}`}
      style={{ height: isVisible ? `${height}px` : '48px' }}
      data-resizing={isResizing}>
      {isVisible && !disabled && <div className={styles.resizeHandle} onMouseDown={handleMouseDown} />}
      <div className={styles.logHeader}>
        <span className={styles.logTitle}>로그</span>
        <IconButton
          onClick={onToggle}
          className={styles.logToggle}
          disabled={disabled}
          tooltip={isVisible ? '로그 닫기' : '로그 열기'}>
          <Icon name={isVisible ? 'arrow-short-down' : 'arrow-short-up'} size={16} />
        </IconButton>
      </div>
      {isVisible && (
        <div className={styles.logContent}>
          <pre className={styles.logPreformatted} ref={logPreRef} onScroll={handleLogScroll}>
            {logs}
          </pre>
        </div>
      )}
    </div>
  );
};
